import React from 'react';
import { redirectToAzureLogin, requestLogout } from './api';
import { startTokenManager, SESSION_STARTED_AT_KEY } from './tokenManager';

type AuthType = 'INTERNAL' | 'EXTERNAL';

export interface PersistedAuthUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email_id: string;
  role_id: number;
  customer_type_id: number;
}

export interface AuthSession {
  token: string;
  authType: AuthType;
  displayName: string;
  email?: string;
  isAdmin?: boolean;
  persisted?: PersistedAuthUser;
  externalContext?: {
    portal?: string;
    uuid?: string;
    ohid?: string;
    clientType?: string;
    policyIds: string[];
  };
}

interface AuthContextValue {
  session: AuthSession | null;
  hydrated: boolean;
  login: (session: AuthSession) => void;
  logout: () => void;
  reauthenticate: () => void;
}

export const AUTH_SESSION_STORAGE_KEY = 'rfi.myp.auth.session';
export const MANUAL_LOGOUT_EVENT = 'rfi:auth:manual-logout';

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = React.useState<AuthSession | null>(null);
  const [hydrated, setHydrated] = React.useState(false);
  const sessionRef = React.useRef<AuthSession | null>(null);

  // Keep ref in sync so the tokenManager closure always reads current session
  sessionRef.current = session;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const raw = window.sessionStorage.getItem(AUTH_SESSION_STORAGE_KEY);

    if (raw) {
      try {
        setSession(JSON.parse(raw) as AuthSession);
        // Ensure the hard-timeout clock is always running for active sessions.
        // Covers cases where SESSION_STARTED_AT_KEY is absent (session predates this
        // feature, or external auth paths that bypass login()). The 60-minute limit
        // then applies from this page load rather than the original login time.
        if (!window.sessionStorage.getItem(SESSION_STARTED_AT_KEY)) {
          window.sessionStorage.setItem(SESSION_STARTED_AT_KEY, String(Date.now()));
        }
      } catch {
        window.sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
      }
    }

    setHydrated(true);
  }, []);

  const login = React.useCallback((nextSession: AuthSession) => {
    setSession(nextSession);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(nextSession));
      if (nextSession.authType === 'EXTERNAL') {
        const serializedPolicyIds = JSON.stringify(nextSession.externalContext?.policyIds ?? []);
        window.sessionStorage.setItem('policy_details', serializedPolicyIds);
      }
      // Record session start time for 60-minute hard timeout
      if (!window.sessionStorage.getItem(SESSION_STARTED_AT_KEY)) {
        window.sessionStorage.setItem(SESSION_STARTED_AT_KEY, String(Date.now()));
      }
    }
  }, []);

  const clearAuthStorage = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    window.sessionStorage.removeItem(SESSION_STARTED_AT_KEY);
    window.sessionStorage.removeItem('policy_details');
  }, []);

  const clearSession = React.useCallback(() => {
    setSession(null);
    clearAuthStorage();
    requestLogout();
  }, [clearAuthStorage]);

  const logout = React.useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(MANUAL_LOGOUT_EVENT));
    }
    clearSession();
    if (typeof window === 'undefined') return;
    if (window.location.pathname !== '/signed-out') {
      window.history.replaceState({}, document.title, '/signed-out');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [clearSession]);

  const reauthenticate = React.useCallback(() => {
    clearSession();
    redirectToAzureLogin();
  }, [clearSession]);

  /**
   * Called by the token manager when a silent token refresh fails.
   * Shows a warning dialog only — does NOT schedule auto-logout.
   * The hard-timeout path at 60 minutes remains the sole forced-logout trigger.
   */
  const notifyRefreshWarning = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('rfi:session:refresh-warning'));
  }, []);

  /** Fires at 57 minutes via the token manager — gives the user a 3-minute window to save work. */
  const notifySessionWarning = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('rfi:session:limit-warning'));
  }, []);

  /**
   * Called by the token manager at exactly 60 minutes.
   * Clears all session data immediately and dispatches the force-logout event.
   * Does NOT redirect to Azure — user must explicitly click Re-authenticate
   * to avoid silent SSO re-login.
   */
  const forceLogout = React.useCallback(() => {
    clearSession();
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('rfi:session:force-logout', { detail: { reason: 'hard-timeout' } }));
  }, [clearSession]);

  // Start the token lifecycle manager once the session is hydrated and active
  React.useEffect(() => {
    if (!session) return;

    const stop = startTokenManager(
      () => {return sessionRef.current},
      (newToken) => {
        setSession((prev) => {return prev ? { ...prev, token: newToken } : prev});
      },
      () => {return notifyRefreshWarning()},
      () => {return notifySessionWarning()},
      () => {return forceLogout()},
    );

    return stop;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!session, forceLogout, notifyRefreshWarning, notifySessionWarning]);

  return (
    <AuthContext.Provider value={{ session, hydrated, login, logout, reauthenticate }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
